import type { ProfileConfig } from "@worker/config-schema";
import { el } from "../../lib/dom";
import { widgetShell, fetchWidget, formatCount } from "./base";

type TwitchWidget = Extract<ProfileConfig["widgets"][number], { type: "twitch" }>;

type TwitchData = {
  user: string;
  login?: string;
  displayName?: string;
  description?: string | null;
  avatarUrl?: string;
  offlineImageUrl?: string | null;
  broadcasterType?: "partner" | "affiliate" | "" | string;
  createdAt?: string;
  live?: boolean;
  viewerCount?: number;
  title?: string | null;
  game?: string | null;
  thumbnailUrl?: string | null;
  tags?: string[];
  lang?: string | null;
  followerCount?: number | null;
  bannerUrl?: string | null;
  unconfigured?: boolean;
  notFound?: boolean;
};

const TYPE_LABEL: Record<string, string> = {
  partner: "Partner",
  affiliate: "Affiliate",
};

export function mountTwitchWidget(cfg: TwitchWidget): HTMLElement {
  const shell = widgetShell({
    kind: "twitch",
    title: "Twitch",
    accent: "#9146ff",
  });
  shell.setLoading();

  const controller = new AbortController();
  shell.root.addEventListener("bio:teardown", () => controller.abort());
  const hide = new Set(cfg.hide ?? []);
  shell.root.setAttribute("data-size", cfg.size ?? "md");

  const render = async () => {
    const env = await fetchWidget<TwitchData>(
      "twitch",
      { u: cfg.username },
      { signal: controller.signal },
    );
    if (!env.ok || !env.data) return shell.setError(env.error ?? "unavailable");
    const d = env.data;
    if (d.unconfigured) return shell.setError("requires TWITCH_CLIENT_ID + SECRET");
    if (d.notFound) return shell.setError(`user not found: ${d.user}`);

    shell.body.innerHTML = "";
    const watchUrl = `https://twitch.tv/${d.login ?? d.user}`;

    if (d.bannerUrl && !hide.has("banner")) {
      shell.body.append(
        el("div", {
          class: "widget-twitch__banner",
          style: `background-image:url(${JSON.stringify(d.bannerUrl)})`,
        }),
      );
    }

    // Top row: avatar + name stack + status pill
    const top = el("div", { class: "widget-twitch__top" });
    if (d.avatarUrl && !hide.has("avatar")) {
      top.append(
        el("img", {
          class: "widget-twitch__avatar",
          src: d.avatarUrl,
          alt: "",
          draggable: false,
        }),
      );
    }
    const names = el("div", { class: "widget-twitch__names" });
    names.append(
      el("div", { class: "widget-twitch__name" }, [d.displayName ?? d.user]),
    );
    const sub = el("div", { class: "widget-twitch__sub" });
    if (d.broadcasterType && TYPE_LABEL[d.broadcasterType]) {
      sub.append(
        el("span", { class: "widget-twitch__badge" }, [TYPE_LABEL[d.broadcasterType]!]),
      );
    }
    sub.append(
      el(
        "span",
        { class: `widget-twitch__status widget-twitch__status--${d.live ? "live" : "off"}` },
        [d.live ? "LIVE" : "offline"],
      ),
    );
    if (d.live) {
      sub.append(
        el("span", { class: "widget-twitch__viewers" }, [
          `${formatCount(d.viewerCount ?? 0)} watching`,
        ]),
      );
    }
    if (typeof d.followerCount === "number" && d.followerCount >= 0 && !hide.has("followers")) {
      sub.append(
        el("span", { class: "widget-twitch__followers" }, [
          `${formatCount(d.followerCount)} followers`,
        ]),
      );
    }
    names.append(sub);
    top.append(names);
    shell.body.append(top);

    // Stream info — same shape whether live or offline (offline uses channel fallback)
    if (d.title) {
      shell.body.append(
        el("div", { class: "widget-twitch__title" }, [d.title]),
      );
    }
    if (d.game) {
      shell.body.append(
        el("div", { class: "widget-twitch__meta" }, [
          d.live ? `Playing ${d.game}` : `Last played: ${d.game}`,
        ]),
      );
    } else if (d.description && !hide.has("bio")) {
      shell.body.append(
        el("div", { class: "widget-twitch__meta" }, [d.description]),
      );
    }

    if (hide.has("button")) return;
    // Watch button
    shell.body.append(
      el(
        "a",
        {
          class: "widget-twitch__btn",
          href: watchUrl,
          target: "_blank",
          rel: "noopener noreferrer",
        },
        [d.live ? "Watch live" : "Visit channel"],
      ),
    );
  };

  void render();
  const interval = window.setInterval(render, 60_000);
  shell.root.addEventListener("bio:teardown", () => window.clearInterval(interval));

  return shell.root;
}
